import { Link } from "react-router";
import { useT } from "../i18n";
import { LEGAL_DOC_IDS, type LegalDocId, legalDoc } from "../legal";
import { usePageMeta } from "../seo";
import { SiteFooter, SiteHeader } from "../site";

/** Privacy policy, terms, data processing agreement and cookie policy, in the visitor's language. */
export function LegalPage({ id }: { id: LegalDocId }) {
  const { t, locale } = useT();
  usePageMeta(id);
  const doc = legalDoc(id, locale);
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <SiteHeader />
      <main className="mx-auto w-full max-w-3xl flex-1 px-6 pt-6 pb-20">
        <article>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{doc.title}</h1>
          <p className="mt-4 text-lg text-slate-600">{doc.intro}</p>
          {doc.sections.map((section) => (
            <section key={section.heading} className="mt-10">
              <h2 className="text-xl font-semibold">{section.heading}</h2>
              {section.paragraphs?.map((p) => (
                <p key={p} className="mt-3 leading-7 text-slate-700">
                  {p}
                </p>
              ))}
              {section.list ? (
                <ul className="mt-3 list-disc space-y-1.5 pl-6 leading-7 text-slate-700">
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
              {section.table ? (
                <div className="mt-4 overflow-x-auto rounded-lg ring-1 ring-slate-200">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-700">
                      <tr>
                        {section.table.head.map((h) => (
                          <th key={h} scope="col" className="px-4 py-2.5 font-semibold">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {section.table.rows.map((row) => (
                        <tr key={row.join("|")}>
                          {row.map((cell, i) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: cells are positional
                            <td key={i} className="px-4 py-2.5 align-top text-slate-700">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>
          ))}
        </article>
        <nav className="mt-16 border-t border-slate-200 pt-6" aria-label={t("legal.other_docs")}>
          <h2 className="text-sm font-semibold text-slate-500">{t("legal.other_docs")}</h2>
          <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
            {LEGAL_DOC_IDS.filter((other) => other !== id).map((other) => (
              <li key={other}>
                <Link to={`/${other}`} className="text-sm font-semibold text-brand-700 hover:underline">
                  {t(`footer.${other}`)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </main>
      <SiteFooter />
    </div>
  );
}
